import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'
import {FormModelType, useForm} from 'reactjs-use-form'

import {Box, Link, Stack, TextField, Theme, Typography} from '@mui/material'
import {PGTypography} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'
import * as Yup from 'yup'

import blueSideBackground from '@progyny/assets/images/side-banner-white-top.png'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import {SidepanelContainer} from '@progyny/components/SidepanelContainer'
import {useRequestResetPasswordMutation} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {RouteEnum, useRoutes} from '@progyny/routes'

import {theme} from '@root/theme'

const ForgetPassword = () => {
  const {classes} = useStyles()
  const location = useLocation<any>()
  const [requestReset, {loading}] = useRequestResetPasswordMutation()
  const {enqueueSnackbar} = useSnackbar()
  const {t} = useTranslation()
  const {goTo} = useRoutes()
  const getErrorMessage = useErrorCodes()
  const emailValidation = Yup.string().email().nullable()
  const isDownSM = useResponsive('down', 'sm')
  const [isSubmitted, setIsSubmitted] = useState(false)

  const formModel: FormModelType = {
    email: {
      value: location?.state?.email || '',
      required: true,
      validator: (email) => {
        if (!emailValidation.isValidSync(email)) {
          return getErrorMessage('invalid-email')
        } else return ''
      },
    },
  }

  const {values, errors, isDisabled, handleOnSubmit, handleOnChange} = useForm(
    formModel,
    handleSubmit
  )

  function handleSubmit() {
    requestReset({
      variables: {
        email: values.email,
      },
    })
      .then(({data}) => {
        if (data?.requestPasswordReset?.errors) {
          enqueueSnackbar({
            message: data?.requestPasswordReset?.errors[0],
            variant: 'error',
          })
        } else {
          setIsSubmitted(true)
          const {email} = values
          enqueueSnackbar({
            message: t(
              `If the email you entered is linked to an account, we've sent you instructions to reset your password.`,
              {email}
            ),
            variant: 'success',
          })
        }
      })
      .catch((error) => {
        if (error.networkError && error.networkError.statusCode === 429) {
          enqueueSnackbar({
            message: t('too_many_requests'),
            variant: 'warning',
          })
        }
      })
  }

  const mobileSuccess = (
    <Box sx={{pb: 16}}>
      <Box
        sx={{
          bgcolor: '#DFFCEB',
          border: '1px solid #456D34',
          borderRadius: 3,
          p: 2,
        }}
      >
        <PGTypography className={classes.successText}>
          {t('recover_password_reset_link_sent')}
        </PGTypography>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.default',
          borderTop: '1px solid',
          borderColor: 'grey.300',
          px: 2,
          pt: 2,
          pb: 4,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <SubmitButton
          type='button'
          fullWidth
          sx={{borderRadius: 3, height: 56}}
          onClick={() => goTo(RouteEnum.LOGIN)}
        >
          {t('recover_password_back_to_log_in')}
        </SubmitButton>
      </Box>
    </Box>
  )

  const formContent = (
    <Stack
      gap={4}
      sx={{
        flexDirection: isDownSM ? 'column' : 'row',
      }}
    >
      <form onSubmit={handleOnSubmit} className={classes.formContainer}>
        {isDownSM ? (
          <Stack gap={6}>
            <Stack gap={2}>
              <PGTypography className={classes.heading}>
                {t('recover_password_forgot_your_password')}
              </PGTypography>
              <PGTypography className={classes.description}>
                {t('recover_password_enter_email_address_you_used')}
              </PGTypography>
            </Stack>
            <TextField
              variant='outlined'
              name='email'
              label={t('recover_password_email_address')}
              value={values.email}
              onChange={handleOnChange}
              error={errors?.email?.hasError}
              helperText={
                errors?.email?.message ? t(errors.email.message) : undefined
              }
              autoComplete='email'
              fullWidth
              inputProps={{'data-testid': 'email'}}
              sx={{'& .MuiOutlinedInput-root': {borderRadius: 2}}}
            />
          </Stack>
        ) : (
          <>
            <Typography
              variant='h1'
              fontWeight={500}
              className={classes.register}
            >
              {t('recover_password_forgot_your_password')}
            </Typography>
            <Typography variant='body1' className={classes.text}>
              {t('recover_password_enter_email_address_you_used')}
            </Typography>
            <TextField
              size='small'
              variant='outlined'
              label={t('recover_password_email_address')}
              margin='dense'
              placeholder={t('yourname@yourcompany.com')}
              name='email'
              value={values.email}
              onChange={handleOnChange}
              error={errors.email.hasError}
              helperText={t(errors.email.message)}
              className={classes.textFiled}
              inputProps={{autoComplete: 'email'}}
            />
          </>
        )}

        {isDownSM ? (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'grey.300',
              px: 2,
              pt: 2,
              pb: 4,
              zIndex: (theme) => theme.zIndex.appBar,
            }}
          >
            <SubmitButton
              type='submit'
              loading={loading}
              disabled={isDisabled}
              fullWidth
              sx={{borderRadius: 3, height: 56}}
            >
              {t('recover_password_send_email')}
            </SubmitButton>
            <Stack
              direction='row'
              justifyContent='center'
              alignItems='center'
              gap={0.5}
              sx={{mt: 3}}
            >
              <PGTypography className={classes.signUpPrompt}>
                {t('recover_password_don_t_have_account')}
              </PGTypography>
              <Link
                onClick={() => goTo(RouteEnum.REGISTER_REQUEST_EMAIL)}
                sx={{cursor: 'pointer'}}
              >
                {t('register_now')}
              </Link>
            </Stack>
          </Box>
        ) : (
          <>
            <SubmitButton
              disabled={isDisabled}
              type='submit'
              loading={loading}
              className={classes.button}
              fullWidth
            >
              {t('recover_password_send_email')}
            </SubmitButton>
            <Box mt={4}>
              <Typography whiteSpace={'nowrap'}>
                {t('recover_password_don_t_have_account')}&nbsp;
                <Link onClick={() => goTo(RouteEnum.REGISTER_REQUEST_EMAIL)}>
                  {t('register_now')}
                </Link>
              </Typography>
            </Box>
          </>
        )}
      </form>
    </Stack>
  )

  return (
    <SidepanelContainer
      hideSidePanel={isDownSM}
      styleProps={{
        backgroundPicSrc: blueSideBackground,
        backgroundColor: theme.palette.primary.main,
        logoColor: 'primary',
      }}
      mainComponent={isDownSM && isSubmitted ? mobileSuccess : formContent}
    />
  )
}

export default ForgetPassword

const useStyles = makeStyles()((theme: Theme) => ({
  button: {
    marginTop: theme.spacing(1),
    display: 'block',
  },
  register: {
    marginBottom: theme.spacing(2.5),
  },
  formContainer: {
    textAlign: 'center',
    width: 420,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingBottom: theme.spacing(20),
    },
    '& h4': {
      fontWeight: 'normal',
    },
  },
  textFiled: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  text: {
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: 'poppins',
    fontWeight: 600,
    fontSize: 24,
    lineHeight: '32px',
    textAlign: 'center',
    display: 'block',
  },
  description: {
    fontFamily: 'DM Sans',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '24px',
    textAlign: 'center',
    color: theme.palette.text.primary,
    display: 'block',
  },
  signUpPrompt: {
    fontFamily: 'DM Sans',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '24px',
    display: 'inline',
  },
  successText: {
    fontFamily: 'DM Sans',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '20px',
    textAlign: 'center',
    color: '#213C15',
    display: 'block',
  },
}))
